import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UsersThree, Plus, Trash, CopySimple } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";

export function GuestAdmin() {
  const { t } = useApp();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openNew, setOpenNew] = useState(false);
  const [email, setEmail] = useState("");
  const [days, setDays] = useState(3);
  const [created, setCreated] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/guests").then((r) => setData(r.data)).catch(() => setData({ total: 0, guests: [], counts: {} }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const STATUS = {
    active: { label: t("guestStatusActive") || "Aktiv", color: "#22C55E" },
    expired: { label: t("guestStatusExpired") || "Abgelaufen", color: "#EF4444" },
    converted: { label: t("guestStatusConverted") || "Konvertiert", color: "var(--app-primary)" },
  };
  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso || "—"; }
  };

  const createGuest = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/guests", { email: email.trim(), days: parseInt(days) || 3 });
      setCreated(r.data);
      setOpenNew(false); setEmail(""); setDays(3);
      toast.success(r.data.guest_email ? "Gastzugang erstellt & E-Mail gesendet" : "Gastzugang erstellt");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  const revoke = async (uid) => {
    if (!window.confirm("Diesen Gastzugang widerrufen? Alle Daten dieses Gasts werden gelöscht.")) return;
    try { await api.post(`/admin/guests/${uid}/revoke`); toast.success("Gastzugang widerrufen"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(created.login_url); toast.success("Login-Link kopiert"); }
    catch { toast.error("Kopieren fehlgeschlagen"); }
  };

  if (!data) return null;

  return (
    <div className="sb-card p-5" data-testid="admin-guests">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <h2 className="font-heading font-semibold flex items-center gap-2"><UsersThree size={22} weight="duotone" style={{ color: "var(--app-strong)" }} />Gastzugänge</h2>
          <p className="font-body text-xs text-gray-500 mt-0.5">Passwortlose Testzugänge erstellen, teilen und widerrufen.</p>
        </div>
        <button data-testid="guest-new-btn" onClick={() => setOpenNew(true)} className="sb-btn px-3 py-2 text-white text-sm font-medium flex items-center gap-1 shrink-0" style={{ background: "var(--app-primary)" }}><Plus size={16} weight="bold" />Neu</button>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="guest-counts">
        {[["active", "Aktiv"], ["expired", "Abgelaufen"], ["converted", "Konvertiert"]].map(([k, l]) => (
          <span key={k} className="text-xs font-heading px-2.5 py-1 rounded-full border-2 border-[var(--app-border)] bg-[var(--app-surface)] text-gray-500">{l}: <b>{data.counts?.[k] || 0}</b></span>
        ))}
      </div>

      {data.guests.length === 0 && <p className="font-body text-sm text-gray-500" data-testid="admin-guests-empty">Noch keine Gastzugänge.</p>}
      <div className="space-y-2">
        {data.guests.map((g, i) => {
          const s = STATUS[g.status] || STATUS.active;
          return (
            <div key={g.user_id} data-testid={`admin-guest-row-${i}`} className="border-2 border-[var(--app-border)] rounded-2xl px-3 py-2 bg-[var(--app-surface)]">
              <div className="flex items-center justify-between gap-2">
                <span className="font-body text-xs text-gray-700 truncate">{g.guest_email || "Ohne E-Mail"}</span>
                <span className="text-[10px] uppercase px-2 py-0.5 rounded-full text-white shrink-0" style={{ background: s.color }} data-testid={`admin-guest-status-${i}`}>{s.label}</span>
              </div>
              <div className="flex items-center justify-between gap-2 mt-1">
                <span className="font-body text-[11px] text-gray-400">{g.buddies} SafeBuddy(s)</span>
                <span className="font-body text-[11px] text-gray-400">
                  {g.status === "converted" ? `Konvertiert: ${fmt(g.converted_at)}` : `Läuft ab: ${fmt(g.expires_at)}`}
                </span>
              </div>
              {g.status !== "converted" && (
                <button data-testid={`guest-revoke-${i}`} onClick={() => revoke(g.user_id)} className="mt-2 sb-btn px-2.5 py-1.5 bg-[var(--app-surface)] text-xs flex items-center gap-1 text-red-500"><Trash size={14} weight="bold" />Widerrufen</button>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent data-testid="guest-new-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Gastzugang erstellen</DialogTitle>
            <DialogDescription>Erstellt einen passwortlosen Testzugang mit Login-Link. E-Mail optional (wird dann verschickt).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-body text-gray-500 uppercase tracking-wider">E-Mail (optional)</label>
              <input data-testid="guest-email-input" type="email" className="sb-input mt-1" placeholder="kunde@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-body text-gray-500 uppercase tracking-wider">Gültigkeit (Tage)</label>
              <input data-testid="guest-days-input" type="number" min="1" max="365" className="sb-input mt-1" value={days} onChange={(e) => setDays(e.target.value)} />
            </div>
            <button data-testid="guest-create-confirm" onClick={createGuest} disabled={busy} className="sb-btn w-full py-3 text-white font-medium disabled:opacity-50" style={{ background: "var(--app-primary)" }}>Erstellen</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!created} onOpenChange={(o) => !o && setCreated(null)}>
        <DialogContent data-testid="guest-created-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Gastzugang bereit</DialogTitle>
            <DialogDescription>Teile diesen Login-Link. Wer ihn öffnet, wird automatisch als Gast angemeldet.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <code data-testid="guest-login-url" className="flex-1 font-mono text-xs break-all bg-gray-50 rounded-lg px-2 py-1.5 text-gray-900">{created?.login_url}</code>
              <button data-testid="guest-copy-link" onClick={copyLink} className="sb-btn p-2 bg-[var(--app-surface)] shrink-0"><CopySimple size={16} weight="bold" /></button>
            </div>
            <button data-testid="guest-created-done" onClick={() => setCreated(null)} className="sb-btn w-full py-2.5 bg-[var(--app-surface)] font-medium">Fertig</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
